import { MoreVertical, RefreshCw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { memo, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/shadcn/dropdown-menu';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';
import { UsersApiAdapter } from '../../adapters/UsersApiAdapter';
import { UserModel } from '../../models/User';
import { ChangeRoleModal } from '../modals/ChangeRoleModal';

const usersAdapter = new UsersApiAdapter(createHttpClient(apiClientWH));

interface DropdownActionsProps {
  user: UserModel;
  onDelete?: () => void;
  onChangeRole?: () => void;
  refetch?: () => void;
}

export const DropdownActions = memo(function DropdownActions({
  user,
  onDelete,
  onChangeRole,
  refetch,
}: DropdownActionsProps) {
  const { t } = useTranslation('usersModule');
  const { toast } = useToast();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const changeRoleModalRef = useRef<ModalCenterRef>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      const response = await usersAdapter.deleteUser(user.id);

      if (response.success) {
        toast({
          title: t('table.deleteSuccess.title'),
          description: response.message || t('table.deleteSuccess.description'),
          variant: 'default',
        });

        // 🎯 Actualizar lista después de la eliminación exitosa
        onDelete?.();
        refetch?.();
      } else {
        toast({
          title: t('table.deleteError.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // 🎯 Extraer mensaje de error como string
      let errorMessage = t('table.deleteError.unexpected');

      if (error?.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (
          errorData.error &&
          typeof errorData.error === 'object' &&
          errorData.error.message &&
          typeof errorData.error.message === 'string'
        ) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      toast({
        title: t('table.deleteError.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    confirmModalRef.current?.open(
      handleDelete,
      undefined,
      t('table.deleteConfirm.title'),
      t('table.deleteConfirm.message', { name: user.name }),
    );
  };

  const handleChangeRoleClick = () => {
    changeRoleModalRef.current?.openModal();
  };

  const handleChangeRoleSuccess = () => {
    onChangeRole?.();
    refetch?.();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="dropdown-trigger"
            aria-label={t('table.actions.menu')}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" data-testid="dropdown-content">
          <DropdownMenuItem onClick={handleChangeRoleClick} data-testid="change-role-button">
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('table.changeRole')}
          </DropdownMenuItem>

          {user.can_be_deleted && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="text-destructive focus:text-destructive"
                data-testid="delete-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeleting ? t('table.deleting') : t('table.delete')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmModal ref={confirmModalRef} />
      <ChangeRoleModal ref={changeRoleModalRef} user={user} onSuccess={handleChangeRoleSuccess} />
    </>
  );
});
